import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig, AxiosError, AxiosHeaders } from 'axios';
import { auth } from '../firebase/config';
import { apiEndpointManager } from './apiEndpointManager';

// Create Axios instance with dynamic base URL
const apiClient: AxiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add the auth token and set the base URL
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Set the base URL dynamically
    config.baseURL = apiEndpointManager.getCurrentUrl();
    
    // Add auth token
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle failover
apiClient.interceptors.response.use(
  // Success handler - if we get a successful response, cache this endpoint
  (response: AxiosResponse) => {
    // If this was the first successful call, cache the working endpoint
    const currentUrl = response.config.baseURL;
    if (currentUrl) {
      const baseUrl = currentUrl.replace('/api/v1', '');
      apiEndpointManager.setWorkingUrl(baseUrl);
    }
    return response;
  },
  // Error handler - implement failover logic
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // On 401, force-refresh the Firebase token and retry once
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !(originalRequest as any)._retryTokenRefresh
    ) {
      const user = auth.currentUser;
      if (user) {
        try {
          const freshToken = await user.getIdToken(true); // force refresh
          if (!originalRequest.headers) {
            originalRequest.headers = new AxiosHeaders();
          }
          originalRequest.headers['Authorization'] = `Bearer ${freshToken}`;
          (originalRequest as any)._retryTokenRefresh = true;
          return await apiClient.request(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        }
      }
    }

    // Check if we should try failover
    if (originalRequest && !(originalRequest as any)._retryWithFallback) {
      // Check if error is a network error or server error (5xx)
      const shouldFailover = !error.response || (error.response.status >= 500 && error.response.status < 600);

      if (shouldFailover && apiEndpointManager.shouldTryFallback()) {
        console.log('Primary API failed, trying fallback...');

        // Switch to fallback
        apiEndpointManager.switchToFallback();

        // Mark as retry via config flag (not a header — avoids CORS preflight issues)
        (originalRequest as any)._retryWithFallback = true;

        // Update the base URL for the retry
        originalRequest.baseURL = apiEndpointManager.getCurrentUrl();
        
        // Retry the request with fallback URL
        try {
          const retryResponse = await apiClient.request(originalRequest);
          console.log('Fallback API succeeded');
          return retryResponse;
        } catch (retryError) {
          console.error('Fallback API also failed:', retryError);
          throw retryError;
        }
      }
    }
    
    // If we can't failover or failover failed, throw the original error
    throw error;
  }
);

// Generic GET request
export const get = async <T>(url: string, params?: any): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.get(url, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Generic POST request
export const post = async <T>(url: string, data: any): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.post(url, data);
    return response.data;
  } catch (error) {
    console.error(`Error in POST request to ${url}:`, error);
    throw error;
  }
};

// Generic PUT request
export const put = async <T>(url: string, data: any): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.put(url, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Generic DELETE request
export const del = async <T>(url: string): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.delete(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default apiClient;
